import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { SSOAuthCode } from '@/lib/types';

export default function SSOCallbackPage() {
  const { handleSSOCallback, isLoading: isLoadingAuth } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (isLoadingAuth || hasProcessed.current) {
      return;
    }
    hasProcessed.current = true;

    const processSSOCallback = async () => {
      try {
        // Extract authorization code and state from URL parameters
        const code = searchParams.get('code');
        const state = searchParams.get('state');
        const error = searchParams.get('error');

        if (error) {
          // SSO app returned an error
          setError(`SSO authentication failed: ${error}`);
          setIsLoading(false);
          return;
        }

        if (!code) {
          setError('No authorization code received from SSO provider');
          setIsLoading(false);
          return;
        }

        // Create auth code object
        const authCode: SSOAuthCode = {
          code,
          state: state || undefined,
          redirect_uri: `${window.location.origin}/auth/sso-callback`,
        };

        // Exchange authorization code for tokens
        const success = await handleSSOCallback(authCode);

        if (success) {
          const pendingInvite = sessionStorage.getItem('pending_invite_token');
          if (pendingInvite) {
            navigate(`/invites/${pendingInvite}`);
          } else {
            navigate('/dashboard');
          }
        } else {
          setError('Failed to authenticate with SSO. Please try again.');
          setIsLoading(false);
        }
      } catch (err) {
        console.error('SSO callback error:', err);
        setError('An unexpected error occurred during SSO authentication.');
        setIsLoading(false);
      }
    };

    processSSOCallback();
  }, [handleSSOCallback, isLoadingAuth, navigate, searchParams]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
              Completing Authentication
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              Please wait while we complete your SSO authentication...
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
              <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
              Authentication Failed
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              {error}
            </p>
            <div className="mt-6">
              <button
                onClick={() => navigate('/auth/login')}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Return to Login
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
}

